import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Clock, Minus, PackageCheck } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { fmtInt } from './dashFormat';

/** Botonera segmentada reutilizable (rango, moneda, medida, …). */
export function SegBar<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ id: T; label: string }>;
}) {
  return (
    <div className="inline-flex rounded-lg border bg-card p-0.5 shadow-sm">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-pressed={value === o.id}
          className={cn(
            'rounded-md px-2.5 py-1 text-sm font-medium transition-colors',
            value === o.id
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Badge de variación: verde sube / rojo baja (color + icono, nunca solo color). */
export function DeltaBadge({ delta, sign }: { delta: string; sign: -1 | 0 | 1 }) {
  const Icon = sign > 0 ? ArrowUpRight : sign < 0 ? ArrowDownRight : Minus;
  const color = sign > 0 ? '#0ca30c' : sign < 0 ? '#d03b3b' : undefined;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-medium tabular-nums',
        sign === 0 && 'text-muted-foreground',
      )}
      style={color ? { color } : undefined}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {delta}
    </span>
  );
}

/** Tarjeta KPI simple (una métrica + delta + pie). */
export function KpiCard({
  label,
  value,
  delta,
  sign,
  foot,
  accent,
}: {
  label: string;
  value: string;
  delta?: string;
  sign?: -1 | 0 | 1;
  foot?: ReactNode;
  /** Color de acento para el valor (p. ej. alerta). */
  accent?: string;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-5">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span
          className="text-2xl font-semibold tracking-tight text-foreground"
          style={accent ? { color: accent } : undefined}
        >
          {value}
        </span>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {delta && sign != null && <DeltaBadge delta={delta} sign={sign} />}
          {foot && <span className="text-xs text-muted-foreground">{foot}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

/** Métrica secundaria en línea, para tarjetas combinadas. */
export function StatInline({
  label,
  value,
  delta,
  sign,
}: {
  label: string;
  value: string;
  delta?: string;
  sign?: -1 | 0 | 1;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-base font-semibold tabular-nums text-foreground">{value}</span>
      {delta && sign != null && <DeltaBadge delta={delta} sign={sign} />}
    </div>
  );
}

/** Bloque de aviso "solo lectura" dentro de una card. */
export function EmptyCard({ children }: { children: ReactNode }) {
  return (
    <Card>
      <CardContent className="py-10 text-center text-sm text-muted-foreground">{children}</CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pendientes con antigüedad (mantenimiento / despacho)
// ---------------------------------------------------------------------------

export type PendienteItem = { cliente: string; fecha: string; dias: number };

/** Color de urgencia por antigüedad del pendiente. */
export function agingColor(dias: number): string {
  if (dias > 60) return '#d03b3b'; // crítico
  if (dias > 30) return '#eb6834'; // serio
  if (dias > 14) return '#eda100'; // atención
  return '#0ca30c'; // ok
}

/** Pill sólido con la antigüedad, coloreado por urgencia. */
export function AgingPill({ dias }: { dias: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums text-white"
      style={{ backgroundColor: agingColor(dias) }}
    >
      <Clock className="h-3 w-3" aria-hidden />
      {fmtInt(dias)} d
    </span>
  );
}

/**
 * Lista de pendientes con impacto visual: cliente + fecha, badge de
 * antigüedad coloreado por urgencia y una barra de aging (proporcional al
 * más viejo) que hace comparables los tiempos de un vistazo.
 */
export function PendientesList({
  items,
  emptyText,
}: {
  items: PendienteItem[];
  emptyText: string;
}) {
  if (items.length === 0) {
    return (
      <div className="flex h-28 flex-col items-center justify-center gap-1 text-sm text-muted-foreground">
        <PackageCheck className="h-5 w-5" aria-hidden />
        {emptyText}
      </div>
    );
  }
  const max = Math.max(1, ...items.map((i) => i.dias));
  return (
    <ol className="flex flex-col gap-3">
      {items.map((it, i) => (
        <li key={it.cliente + i} className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-foreground" title={it.cliente}>
                {it.cliente}
              </div>
              <div className="text-xs text-muted-foreground">{it.fecha || '—'}</div>
            </div>
            <AgingPill dias={it.dias} />
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full"
              style={{ width: `${(it.dias / max) * 100}%`, backgroundColor: agingColor(it.dias) }}
            />
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Skeleton de tarjetas KPI. */
export function KpiSkeletonRow({ n = 4 }: { n?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: n }).map((_, i) => (
        <Card key={i}>
          <CardContent className="flex flex-col gap-2 p-5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-28" />
            <Skeleton className="h-3 w-20" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
